import { useCallback } from 'react';
import { SSE_BASE_URL } from '@/lib/api';
import { useEventStream, type EventStreamStatus } from '@/hooks/useEventStream';
import { workflowSSEHandlers } from '@/stores/workflow-store';
import { registrationSSEHandlers } from '@/stores/registration-store';
import { workflowAuditSSEHandlers } from '@/stores/workflow-audit-store';
import type { WorkflowActionSSEEvent } from '@/lib/workflow-audit';
import type {
  WorkflowStatusEvent,
  DagNodeEvent,
  WorkflowToolActivityEvent,
  LoopIterationEvent,
  WorkflowAuditEvent,
} from '@/lib/types';

const DASHBOARD_STREAM_URL = `${SSE_BASE_URL}/api/stream/__dashboard__`;

/**
 * Connects to the multiplexed dashboard SSE stream and routes events to the
 * Zustand store. Connection lifecycle (reconnect/backoff/foreground+online
 * resume) is owned by {@link useEventStream}.
 *
 * Returns the stream `status` so callers can surface a permanently-dead stream
 * (otherwise dashboard workflow cards freeze silently).
 */
export function useDashboardSSE(): { status: EventStreamStatus } {
  const handleMessage = useCallback((e: MessageEvent): void => {
    let event: { type: string };
    try {
      event = JSON.parse(e.data as string) as { type: string };
    } catch (err) {
      console.error('[Dashboard SSE] Failed to parse event', {
        raw: e.data,
        error: err instanceof Error ? err.message : err,
      });
      return;
    }

    switch (event.type) {
      case 'workflow_status':
        workflowSSEHandlers.onWorkflowStatus(event as WorkflowStatusEvent);
        break;
      case 'dag_node':
        workflowSSEHandlers.onDagNode(event as DagNodeEvent);
        break;
      case 'workflow_tool_activity':
        workflowSSEHandlers.onToolActivity(event as WorkflowToolActivityEvent);
        break;
      case 'workflow_step':
        workflowSSEHandlers.onLoopIteration(event as LoopIterationEvent);
        break;
      case 'workflow_audit':
        workflowAuditSSEHandlers.onWorkflowAudit(event as WorkflowAuditEvent);
        break;
      case 'workflow_action':
        workflowAuditSSEHandlers.onWorkflowAction(event as WorkflowActionSSEEvent);
        break;
      case 'registration_start':
        registrationSSEHandlers.onRegistrationStart(
          event as { type: 'registration_start'; path: string }
        );
        break;
      case 'registration_complete':
        registrationSSEHandlers.onRegistrationComplete(
          event as { type: 'registration_complete'; path: string; codebaseName: string }
        );
        break;
      case 'registration_failed':
        registrationSSEHandlers.onRegistrationFailed(
          event as { type: 'registration_failed'; path: string; error: string }
        );
        break;
      case 'registration_env_leak':
        registrationSSEHandlers.onRegistrationEnvLeak(
          event as {
            type: 'registration_env_leak';
            path: string;
            detectedKeys: { name: string; file: string }[];
          }
        );
        break;
      case 'registration_progress':
        registrationSSEHandlers.onRegistrationProgress(
          event as {
            type: 'registration_progress';
            completed: number;
            failed: number;
            total: number;
          }
        );
        break;
      case 'registration_batch_complete':
        registrationSSEHandlers.onRegistrationBatchComplete(
          event as { type: 'registration_batch_complete'; batchId: string }
        );
        break;
      // heartbeat — ignore
    }
  }, []); // stable handlers from Zustand module level

  const { status } = useEventStream({ url: DASHBOARD_STREAM_URL, onMessage: handleMessage });
  return { status };
}
